/**
 * Every string the self-schedule day-header / week checkbox can show
 * when it is disabled, in one place.
 *
 * Before ENG-3642 a single sentence — "Interviewer is not available on
 * this day" — covered four different causes, so it was unfalsifiable
 * for the person reading it. Each cause now names itself, and names the
 * interviewer it is about, so a recruiter knows which setting to go and
 * change. Wording is the signed-off table on the ticket.
 */
import type { DayUnavailableReason } from '../state/dayUnavailableReason';
import type { InterviewKind } from '../../../availability';

export const DISABLED_NO_INTERVIEWER = 'Pick an interviewer to select slots';
export const DISABLED_PAST = 'Cannot select slots in the past';
export const DISABLED_WEEK_NO_AVAILABILITY =
  'No open slots this week. Try a different week.';

const AVAILABILITY_LOAD_FAILED =
  'Availability could not be loaded. Refresh the page to try again.';

/**
 * Stand-in for a display name we could not resolve. An interviewer id
 * in a tooltip is worse than saying nothing specific, so every named
 * sentence degrades to this rather than leaking one.
 */
const UNNAMED_SINGLE = 'The interviewer';
const UNNAMED_MANY = 'The interviewers';

/**
 * "2 days" reads better than "48 hours" for whole-day notice periods.
 * Shared with `MinimumNoticeHoverPopover` so the tooltip and the
 * hover card can never disagree about the same setting.
 */
export function formatNoticeLabel(hours: number): string {
  if (hours <= 0) return '0 hours';
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/**
 * "A", "A and B", "A, B and C", then "A, B and 3 others" — a tooltip
 * that lists a nine-person panel by name stops being readable, and the
 * recruiter only needs enough to recognise who to chase.
 */
export function formatInterviewerNames(names: string[]): string {
  const clean = names.filter(Boolean);
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  if (clean.length === 3) return `${clean[0]}, ${clean[1]} and ${clean[2]}`;
  return `${clean[0]}, ${clean[1]} and ${clean.length - 2} others`;
}

export interface BuildDayDisabledCopyArgs {
  reason: DayUnavailableReason;
  interviewerCount: number;
  interviewKind: InterviewKind;
  minimumNoticeHours: number;
  durationMinutes: number;
  /** Display names of the selected interviewers, in roster order. */
  interviewerNames?: string[];
  /**
   * Panel only — the subset with no working hours on this day. These
   * are the people the recruiter has to go and fix, so they get named
   * instead of the whole panel.
   */
  unavailableInterviewerNames?: string[];
}

export function buildDayDisabledCopy({
  reason,
  interviewerCount,
  interviewKind,
  minimumNoticeHours,
  durationMinutes,
  interviewerNames = [],
  unavailableInterviewerNames,
}: BuildDayDisabledCopyArgs): string {
  const isSingle = interviewerCount <= 1;
  // A one-person "panel" behaves like a 1:1 for wording purposes.
  const isPanel = interviewKind === 'panel' && !isSingle;
  const solo = isSingle ? (interviewerNames[0] ?? '') : '';

  switch (reason) {
    case 'outside_availability': {
      if (isSingle) {
        return `${solo || UNNAMED_SINGLE} is not available on this day`;
      }
      // A 1:1 pool would need every member listed to be truthful — the
      // day is only dead when NOBODY was free — so it stays collective.
      if (!isPanel) return 'No interviewer is available on this day';
      const blocked = (unavailableInterviewerNames ?? []).filter(Boolean);
      if (blocked.length === 0) {
        return 'Some interviewers are not available on this day';
      }
      const verb = blocked.length > 1 ? 'are' : 'is';
      return `${formatInterviewerNames(blocked)} ${verb} not available on this day`;
    }
    case 'no_overlap': {
      const named = interviewerNames.filter(Boolean);
      const everyone = formatInterviewerNames(named);
      if (!everyone) {
        return `${UNNAMED_MANY} have no overlapping working hours on this day`;
      }
      // Grammar guard only — the resolver withholds this reason below
      // two interviewers, so the singular branch should be unreachable.
      const verb = named.length === 1 ? 'has' : 'have';
      return `${everyone} ${verb} no overlapping working hours on this day`;
    }
    case 'calendar_busy':
      if (isSingle) {
        return `${solo || UNNAMED_SINGLE}'s calendar is fully booked on this day`;
      }
      return isPanel
        ? "Some interviewers' calendars are fully booked on this day"
        : "Every interviewer's calendar is fully booked on this day";
    case 'too_soon':
      // The notice window is an account setting, not a fact about a
      // person — naming an interviewer here sends the reader to the
      // wrong screen. The cutoff itself is the actionable part.
      if (minimumNoticeHours > 0) {
        return `Minimum notice is set to ${formatNoticeLabel(
          minimumNoticeHours
        )}. Select slots beyond that window.`;
      }
      return isSingle
        ? `${solo || UNNAMED_SINGLE} has no slots left today`
        : 'No slots left today';
    case 'no_fit':
      return isSingle
        ? `${solo || UNNAMED_SINGLE} has no free ${durationMinutes}-minute block on this day`
        : `No free ${durationMinutes}-minute block on this day`;
    case 'unknown':
    default:
      return AVAILABILITY_LOAD_FAILED;
  }
}
